import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { asapScheduler } from 'rxjs';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';

import { ButtonComponent } from '../../../components/button/button.component';
import { FlyoutMenuComponent } from '../../../components/flyout-menu/flyout-menu.component';
import { FlyoutMenuHeaderComponent } from '../../../components/flyout-menu/flyout-menu-header.component';
import { FlyoutMenuItemDirective } from '../../../components/flyout-menu/flyout-menu-item.directive';
import { IconComponent } from '../../../components/icon/icon.component';
import { BadgeComponent } from '../../../components/badge/badge.component';
import { HrefDirective, HrefNavigationEvent } from '../../../directives/href/href.directive';
import { Menu, MenuGroupItem, MenuItem, MenuPanelItem } from '../../../domain/menu.domain';
import { MessagesCounterService } from '../../../services/messages/messages-counter.service';
import { SessionStorageService } from '../../../services/storage/session-storage.service';
import { SecurityService } from '../../../services/security.service';
import { DataAnalyticsService } from '../../../services/data-analytics/data-analytics.service';
import { IfFeatureDirective } from '../../if-feature/if-feature.directive';
import { IfNoFeatureDirective } from '../../if-feature/if-no-feature.directive';
import { MessagesCounterComponent } from '../../messages/messages-counter/messages-counter.component';
import { ChatService } from '../../chat/chat.service';
import { MenuItemContentComponent } from '../item-content/item-content.component';
import { MenuItemToggleComponent } from '../item-toggle/item-toggle.component';
import { MenuItemComponent } from '../item/item.component';
import { MenuNavigationBaseComponent } from '../menu-navigation-base.component';
import { MenuTopGlobalNavigationItemLabelComponent } from '../top-navigation-item/top-navigation-item-label.component';
import { MenuTopGlobalNavigationItemComponent } from '../top-navigation-item/top-navigation-item.component';

/**
 * Top Navigation Component
 *
 * This is the top navigation for Menu Component
 */
@Component({
  selector: 'td-wb-menu-top-navigation',
  templateUrl: 'top-navigation.component.html',
  styleUrls: ['top-navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateDirective,
    TranslatePipe,
    ButtonComponent,
    HrefDirective,
    IconComponent,
    MenuItemComponent,
    MenuItemToggleComponent,
    MenuItemContentComponent,
    NgClass,
    NgTemplateOutlet,
    MenuTopGlobalNavigationItemComponent,
    MenuTopGlobalNavigationItemLabelComponent,
    MessagesCounterComponent,
    IfNoFeatureDirective,
    IfFeatureDirective,
    FlyoutMenuComponent,
    FlyoutMenuHeaderComponent,
    FlyoutMenuItemDirective,
    BadgeComponent
  ],
  encapsulation: ViewEncapsulation.None
})
export class MenuTopNavigationComponent extends MenuNavigationBaseComponent implements OnChanges {
  private static readonly NEW_BADGE_PERMISSION: string = 'NOTIFICATION_PREFERENCE';
  /**
   * Client name
   */
  @Input() clientName: string;

  /**
   * Logout Item
   */
  @Input() logoutItem: MenuPanelItem;

  /**
   * Navigation Menu
   */
  @Input() navigationMenu: Menu;

  /**
   * Boolean for determining whether top menu is enabled
   */
  @Input() isTopMenuEnabled: boolean;

  isFlyoutMenuOpened: boolean;

  customizeSiteMenuItem: MenuItem;

  documentsMenuItem: MenuItem;

  messagesMenuItem: MenuItem;

  profileMenuItem: MenuItem;

  secureMessageMenuItem: MenuItem;

  feedBackMenuItem: MenuItem;

  messageCounterSpacing: boolean;

  showChat: boolean = false;

  constructor(ngZone: NgZone,
              changeDetectorRef: ChangeDetectorRef,
              private messagesCounterService: MessagesCounterService,
              private sessionStorageService: SessionStorageService,
              private chatService: ChatService,
              private readonly securityService: SecurityService,
              private dataAnalyticsService: DataAnalyticsService) {
    super(ngZone, changeDetectorRef);
    this.messagesCounterService.onMessagesCounterUpdate
      .pipe(takeUntilDestroyed())
      .subscribe((counter: number): void => {
        this.messageCounterSpacing = counter > 0;
        changeDetectorRef.markForCheck();
      })
    this.showChat = this.chatService.hasAccessToChat();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.navigationMenu !== undefined && changes.navigationMenu.currentValue !== undefined) {
      this.onTopNaviationChange();
    }
  }

  onFlyoutMenuOpen(): void {
    this.isFlyoutMenuOpened = true;
  }

  onFlyoutMenuClose(): void {
    this.isFlyoutMenuOpened = false;
  }

  isExternalLink(item: MenuPanelItem): boolean {
    return item.href !== undefined && !!item.target;
  }

  onTopNaviationChange(): void {
    const topNavigation = this.navigationMenu.topNavigation;
    const userMenu = topNavigation.find((m: MenuPanelItem): boolean => m.id === 'user');
    if (userMenu !== undefined && userMenu.menu !== undefined && userMenu.menu[0] !== undefined) {
      const userProfileMenus = userMenu.menu[0].item;
      this.customizeSiteMenuItem = userProfileMenus.find((m: MenuGroupItem & MenuItem): boolean => m.id === '1' || m.id === '2');
      this.documentsMenuItem = userProfileMenus.find((m: MenuGroupItem & MenuItem): boolean => m.id === '3');
      this.messagesMenuItem = userProfileMenus.find((m: MenuGroupItem & MenuItem): boolean => m.id === '4');
      this.profileMenuItem = userProfileMenus.find((m: MenuGroupItem & MenuItem): boolean => m.id === '5');
      this.secureMessageMenuItem = userProfileMenus.find((m: MenuGroupItem & MenuItem): boolean => m.id === '6');
    }

    const contactUsMenu = topNavigation.find((menu: MenuPanelItem): boolean => menu.id === 'contactUs');
    if (contactUsMenu !== undefined && contactUsMenu.menu !== undefined && contactUsMenu.menu[0] !== undefined) {
      this.feedBackMenuItem = contactUsMenu.menu.find((m: MenuGroupItem & MenuItem): boolean => m.id === 'weWantToHearFromYou').item[0];
    }
  }

  openChat(): void {
    this.dataAnalyticsService.emitLinkClickEvent('contactus:chatwithaliveagent');
    this.chatService.openChat('Contact Us');
  }

  showNewBadge(): boolean {
    return this.securityService.hasPermission(MenuTopNavigationComponent.NEW_BADGE_PERMISSION);
  }

  protected logout(ref: HrefNavigationEvent): void {
    this.chatService.closeChat();
    this.sessionStorageService.clearSessionStorage();
    asapScheduler.schedule(() => ref?.navigate());
  }
}
